/**
 * Everything the views are allowed to do.
 *
 * Kept in its own module so a screen can depend on the shape of an action
 * without importing the wiring that implements it — which is what stops
 * `app.ts` and `screens/*.ts` from becoming a cycle.
 */
export interface Actions {
  setInput(value: string): void;
  useExample(url: string): void;
  submit(): void;
  /**
   * Start the crawl the person came back for, on their press and no sooner.
   *
   * A separate member from `submit()` because the two are answering different
   * questions: `submit()` is "crawl what is in the field", `resume()` is "crawl
   * the address the sign-in link carried". Keeping them apart is what makes it
   * impossible for the boot path to reach a crawl by accident.
   */
  resume(): void;
  /** Leave the sign-in step for the landing screen, keeping the typed address. */
  cancelSignIn(): void;
  copy(id: string, text: string): void;
  toggleAck(): void;
  setPublishSecret(value: string): void;
  publish(): void;
  restart(): void;
}

/**
 * Accept what an operator actually types.
 *
 * People paste `cinestar.hr`, `www.cinestar.hr/raspored`, and occasionally a
 * whole URL with a query string. All three are fine. What is not fine is a bare
 * word with no dot, which is the typo case worth catching before spending a
 * crawl on it.
 */
export function normalizeUrl(raw: string): { url: string } | { error: string } {
  const value = raw.trim();
  if (!value) return { error: 'Enter a website address, e.g. cinestar.hr' };
  if (/\s/.test(value)) return { error: 'That has a space in it. Enter one address.' };

  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return { error: `Could not read “${value}” as a web address.` };
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { error: 'Only http and https addresses can be crawled.' };
  }
  if (!parsed.hostname.includes('.')) {
    return { error: `“${parsed.hostname}” is missing a domain ending, e.g. .hr or .com` };
  }
  return { url: parsed.toString() };
}
